import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { PERSON_UPLOAD_CREDITS } from "@signalist/shared-types";

import { Modal } from "@/components/Modals/Modal";

export interface RetryOthersModalProps {
	/** The other people the list's run would also search; null while closed. */
	pendingOthers: number | null;
	isLoading: boolean;
	onConfirm: () => void;
	onClose: () => void;
}

/**
 * A lead's Retry re-runs its list, and that run also searches the list's other waiting people,
 * each one searched charged — also when no one is found (PREC-4520). The button says so before it
 * starts, and only searches once the user agreed.
 */
export const RetryOthersModal: FunctionComponent<RetryOthersModalProps> = ({
	pendingOthers,
	isLoading,
	onConfirm,
	onClose,
}) => {
	const { t } = useTranslation();

	// The shared Modal keeps its dialog mounted when closed; unmount fully instead.
	if (pendingOthers === null) {
		return null;
	}

	return (
		<Modal
			cancelButtonText={t("inbox.enrichAnywayFlow.cancel")}
			handleClose={onClose}
			handleSave={onConfirm}
			isLoading={isLoading}
			isOpened
			size="sm"
			submitButtonText={t("inbox.enrichAnywayFlow.retryOthers.confirm")}
			// A yes/no question that guards a charge: Escape must reach it without a click inside.
			takeFocus
			title={t("inbox.enrichAnywayFlow.retryOthers.title")}
		>
			<p className="text-sm text-text-meta" data-test="retry-others-modal">
				{t("inbox.enrichAnywayFlow.retryOthers.description", {
					count: pendingOthers,
					credits: PERSON_UPLOAD_CREDITS,
				})}
			</p>
		</Modal>
	);
};
